import { computed, onBeforeMount, onMounted, ref, watch } from 'vue'
import { createInjectionState } from '@vueuse/shared'
import type { Stripe } from 'stripe'
import { PromiseQueue } from '#root/lib/promise-queue'
import { useCartSession } from '#root/lib/useCartSession'
import { useOccasionsStoreOrThrow } from '#root/lib/useOccasionsStore'
import { useVariantsStoreOrThrow } from '#root/lib/useVariantsStore'
import { logger } from '#root/lib/logger'
import { tracker } from '#root/lib/tracker'
import {
  fnLoadCart,
  fnLoadCheckout,
  fnLoadInformation,
  fnSetLineItem,
  fnSubmitCheckout,
  fnSubmitInformation,
  fnUpdatePurchaser,
  fnUpdateRecipients
} from '#root/rpc/client/cart'
import { floatCents, parseCents } from '#root/lib/utils'
import type {
  AgreementResponses,
  CartDisplayItem,
  CartResponse,
  ContactInfo,
  RecipientInfo,
  SurveyResponses
} from '#root/types/cart'

// TODO: Implement https://vueuse.org/core/useBroadcastChannel/

function errorCartResponse(err: unknown): CartResponse {
  if (
    err &&
    typeof err === 'object' &&
    'message' in err &&
    typeof (err as { message: string }).message === 'string'
  ) {
    return {
      cart: null,
      message: (err as { message: string }).message,
      ok: false
    }
  }
  return { cart: null, message: 'Unknown error', ok: false }
}

const [useProvideCartStore, useCartStore] = createInjectionState(
  (isInfo: boolean = false) => {
    const pq = new PromiseQueue()
    const isFetching = ref(false)
    const isPending = ref(false)
    const isPurchase = ref(false)
    const updates = ref(new Map<string, string>())
    const {
      cartCount,
      cartId,
      cartReturnTo,
      cartToken,
      infoCartId,
      infoCartToken,
      sessionParams,
      sessionReferrer,
      sessionUpdateTime,
      testMode
    } = useCartSession()
    const {
      fetchTime,
      findOccasions,
      isUpcoming,
      items: occasionItems,
      uniqueIndexes: occasionUniqueIndexes
    } = useOccasionsStoreOrThrow()
    const { items: variantItems, uniqueIndexes: variantUniqueIndexes } =
      useVariantsStoreOrThrow()

    const data = ref<CartResponse | null>(null)
    const email = ref<string | null>(null)
    const items = computed<CartDisplayItem[] | null>(() => {
      let result: CartDisplayItem[] | null = null

      if (
        data.value &&
        data.value.cart &&
        data.value.cart.line_items &&
        occasionItems.value &&
        occasionUniqueIndexes.value &&
        variantItems.value &&
        variantUniqueIndexes.value
      ) {
        result = []

        for (const lineItem of data.value.cart.line_items) {
          const occasionIndex =
            occasionUniqueIndexes.value.product_occasion_sku &&
            occasionUniqueIndexes.value.product_occasion_sku[
              lineItem.sku.toUpperCase()
            ]

          if (typeof occasionIndex === 'number') {
            const occasion = occasionItems.value[occasionIndex]
            if (!occasion) continue

            const item: CartDisplayItem = {
              occasion,
              price: lineItem.price,
              price_id: lineItem.price_id,
              price_type: lineItem.price_type,
              quantity: 0,
              recipients: lineItem.recipients || [],
              sku: lineItem.sku,
              source: lineItem.source
            }

            switch (occasion.schedule_status) {
              case 'active':
                item.meta = lineItem.meta
                item.quantity = lineItem.quantity
                break
              case 'cancelled':
              case 'postponed':
              case 'voided':
                item.meta = {
                  alert: {
                    text: `This occasion was ${occasion.schedule_status}.`,
                    type: 'warning'
                  },
                  is_disabled: true
                }
                break
              default:
                item.meta = {
                  alert: {
                    text: 'This occasion is unavailable.',
                    type: 'warning'
                  },
                  is_disabled: true
                }
            }

            result.push(item)
            continue
          }

          const variantIndex =
            variantUniqueIndexes.value.product_variant_sku &&
            variantUniqueIndexes.value.product_variant_sku[
              lineItem.sku.toUpperCase()
            ]

          if (typeof variantIndex === 'number') {
            const variant = variantItems.value[variantIndex]
            if (!variant) continue

            const item: CartDisplayItem = {
              variant,
              price: lineItem.price,
              price_id: lineItem.price_id,
              price_type: lineItem.price_type,
              quantity: lineItem.quantity,
              recipients: lineItem.recipients || [],
              sku: lineItem.sku,
              source: lineItem.source
            }

            item.meta = lineItem.meta
            item.quantity = lineItem.quantity

            result.push(item)
            continue
          }
        }
      }

      return result
    })

    const hasShopQuantity = computed(() => {
      return !items.value
        ? false
        : items.value.reduce<boolean>((acc, cur) => {
            if (cur.quantity > 0) return true
            return acc
          }, false)
    })

    const isEditing = computed(() => updates.value.size > 0)

    const subtotal = computed(() => {
      return !items.value
        ? 0
        : floatCents(
            items.value.reduce((acc, cur) => {
              if (typeof cur.price === 'string')
                acc += parseCents(cur.price) * cur.quantity
              return acc
            }, 0)
          )
    })

    async function loadCart(): Promise<CartResponse> {
      if (data.value) return data.value

      const resp: CartResponse = await fnLoadCart(
        testMode.value,
        cartId.value,
        cartToken.value
      )

      setCartIdAndToken(resp)
      setData(resp)
      setEmail(resp)

      tracker.plausibleEvent('loadCart', {
        testMode: testMode.value,
        cartId: cartId.value || ''
      })

      return resp
    }

    async function loadCheckout(): Promise<CartResponse> {
      if (data.value) return data.value

      const resp: CartResponse = await fnLoadCheckout(
        testMode.value,
        cartId.value,
        cartToken.value
      )

      setCartIdAndToken(resp)
      setData(resp)
      setEmail(resp)

      tracker.plausibleEvent('loadCheckout', {
        testMode: testMode.value,
        cartId: cartId.value || ''
      })

      tracker.linkedinTrack(20454388)
      tracker.facebookEvent('InitiateCheckout')
      tracker.googleAdsConversion('zVxRCJHiivIaEPPHwtgD')

      return resp
    }

    async function loadInformation(): Promise<CartResponse> {
      if (data.value) return data.value

      const resp: CartResponse = await fnLoadInformation(
        testMode.value,
        infoCartId.value,
        infoCartToken.value
      )

      setData(resp)
      setEmail(resp)

      if (isPurchase.value && resp.cart && resp.cart.order_number) {
        const currency = 'USD'
        const value = resp.cart.line_items.reduce((acc, cur) => {
          acc += parseFloat(cur.price) * cur.quantity
          return acc
        }, 0)

        tracker.plausibleEvent('purchase', {
          testMode: testMode.value,
          cartId: infoCartId.value || '',
          currency,
          orderNumber: resp.cart.order_number,
          value
        })

        tracker.googleEvent('purchase', {
          currency,
          transaction_id: resp.cart.order_number,
          items: resp.cart.line_items.map(item => ({
            item_id: item.sku,
            price: parseFloat(item.price),
            quantity: item.quantity
          })),
          value
        })

        const advertisingIdentity = await tracker.getAdvertisingIdentity(
          email.value
        )

        tracker.linkedinTrack(20454396)
        tracker.facebookInit(advertisingIdentity)
        tracker.facebookEvent('Purchase', {
          currency,
          order_id: resp.cart.order_number,
          content_ids: resp.cart.line_items.map(item => item.sku),
          content_type: 'product',
          value
        })
        tracker.googleAdsConversion('n8ZwCJy1rpkaEPPHwtgD', {
          currency,
          transaction_id: resp.cart.order_number,
          user_data: {
            email: advertisingIdentity?.emailHash ?? null
          },
          value
        })
      } else {
        tracker.plausibleEvent('loadInformation', {
          testMode: testMode.value,
          cartId: infoCartId.value || ''
        })
      }

      return resp
    }

    async function addItem(
      sku: string,
      price: string,
      priceId: string,
      priceType: string,
      source: string,
      eventName: string
    ): Promise<CartResponse | undefined> {
      return pq
        .add(async () => {
          await loadCart()

          if (!(data.value && data.value.cart)) return

          const ind = data.value.cart.line_items.findIndex(
            item => item.sku === sku
          )
          if (ind > -1) return

          const quantity = 1

          data.value.cart.line_items.push({
            meta: {
              is_pending: true
            },
            price,
            price_id: priceId,
            price_type: priceType,
            quantity,
            sku,
            source
          })

          const resp: CartResponse = await fnSetLineItem(
            testMode.value,
            cartId.value,
            cartToken.value,
            sku,
            {
              price,
              price_id: priceId,
              price_type: priceType,
              quantity,
              source
            }
          )

          setCartIdAndToken(resp)
          setData(resp)
          setEmail(resp)

          tracker.plausibleEvent('addItem', {
            testMode: testMode.value,
            cartId: cartId.value || '',
            currency: 'USD',
            price,
            priceType,
            sku,
            source
          })

          if (eventName) {
            tracker.posthogCapture(`ecom:${eventName}`, {
              test_mode: testMode.value,
              cart_id: cartId.value || '',
              currency: 'USD',
              price: parseFloat(price),
              price_type: priceType,
              quantity,
              sku,
              source
            })
          }

          tracker.googleEvent('add_to_cart', {
            currency: 'USD',
            items: [
              {
                item_id: sku,
                price: parseFloat(price),
                quantity
              }
            ],
            value: parseFloat(price)
          })

          tracker.linkedinTrack(20454380)
          tracker.facebookEvent('AddToCart', {
            currency: 'USD',
            content_ids: [sku],
            content_type: 'product',
            value: parseFloat(price)
          })
          tracker.googleAdsConversion('7u35CJTiivIaEPPHwtgD')

          return resp
        })
        .catch(err => errorCartResponse(err))
    }

    async function addItemBySKU(
      sku: string
    ): Promise<CartResponse | undefined> {
      const occData = await findOccasions(fetchTime.value)
      if (!(occData.data && occData.unique_indexes)) return

      const ind =
        occData.unique_indexes.product_occasion_sku &&
        occData.unique_indexes.product_occasion_sku[sku.toUpperCase()]
      if (typeof ind !== 'number') return

      const occasion = occData.data[ind]
      if (!occasion) return

      if (!isUpcoming(occasion, fetchTime.value)) return

      const listPrice = occasion.list_price_usd ?? ''
      const stripePriceId = occasion.stripe_list_price_id ?? ''
      const occasionSku = occasion.sku ?? ''
      if (!occasionSku || !listPrice || !stripePriceId) return

      return addItem(
        occasionSku,
        listPrice,
        stripePriceId,
        'list',
        'shop',
        'item_by_sku_add'
      )
    }

    async function beforeUpdateQuantity(sku: string): Promise<void> {
      if (!(data.value && data.value.cart)) return
      updates.value.set(sku, '')
    }

    function emptyCart(): void {
      cartCount.value = 0
      cartId.value = null
      cartToken.value = null
      sessionParams.value = null
      sessionReferrer.value = ''
      sessionUpdateTime.value = 0
      data.value = null
      email.value = null
    }

    function fetchCart(): Promise<CartResponse> {
      isFetching.value = true
      return pq.add(loadCart).finally(() => (isFetching.value = false))
    }

    function fetchCheckout(): Promise<CartResponse> {
      isFetching.value = true
      return pq.add(loadCheckout).finally(() => (isFetching.value = false))
    }

    function fetchInformation(): Promise<CartResponse> {
      isFetching.value = true
      return pq.add(loadInformation).finally(() => (isFetching.value = false))
    }

    async function removeItem(sku: string): Promise<CartResponse | undefined> {
      return pq
        .add(async () => {
          await loadCart()

          if (!(data.value && data.value.cart)) return

          const ind = data.value.cart.line_items.findIndex(
            item => item.sku === sku
          )
          const lineItem =
            ind > -1 ? data.value.cart.line_items[ind] : undefined
          if (lineItem) {
            lineItem.meta = {
              is_pending: true
            }
          }

          const resp: CartResponse = await fnSetLineItem(
            testMode.value,
            cartId.value,
            cartToken.value,
            sku,
            { quantity: -1 }
          )

          setCartIdAndToken(resp)
          setData(resp)

          tracker.plausibleEvent('removeItem', {
            testMode: testMode.value,
            cartId: cartId.value || '',
            sku
          })

          return resp
        })
        .catch(err => errorCartResponse(err))
    }

    function setCartIdAndToken(resp: CartResponse): void {
      if (!(resp && resp.cart)) return
      cartId.value = resp.cart.id
      cartToken.value = resp.cart.token
    }

    function setData(resp: CartResponse): void {
      if (!(resp && resp.cart)) return
      data.value = resp
    }

    function setEmail(resp: CartResponse): void {
      if (!(resp && resp.cart)) return
      email.value =
        resp.cart.purchaser && typeof resp.cart.purchaser.email === 'string'
          ? resp.cart.purchaser.email
          : null
    }

    async function submitCheckout(
      location: string,
      payment: boolean
    ): Promise<CartResponse | undefined> {
      isPending.value = true

      return pq
        .add(async () => {
          await loadCart()

          if (!(data.value && data.value.cart)) return
          if (typeof email.value !== 'string') return
          if (!items.value) return

          const lineItems = items.value
            .filter(item => item.quantity > 0)
            .map(item => ({
              price: item.price,
              price_id: item.price_id,
              price_type: item.price_type,
              quantity: item.quantity,
              sku: item.sku,
              source: item.source
            }))

          const metadata: Stripe.MetadataParam = {}

          if (sessionParams.value) {
            for (const [key, value] of Object.entries(sessionParams.value)) {
              if (
                (key === 'aff' ||
                  key.startsWith('gad_') ||
                  key.startsWith('hsa_') ||
                  key.startsWith('utm_')) &&
                typeof value === 'string'
              ) {
                metadata[key] = value
              }
            }
          }

          if (sessionReferrer.value) metadata.referrer = sessionReferrer.value

          tracker.plausibleEvent('submitCheckout', {
            testMode: testMode.value,
            cartId: cartId.value || '',
            payment
          })

          const distinctId = await tracker.posthogIdentify(email.value)
          tracker.posthogCapture(
            payment
              ? 'ecom:checkout_to_payment_submit'
              : 'ecom:checkout_email_submit',
            {
              test_mode: testMode.value,
              cart_id: cartId.value || ''
            }
          )

          const clientId = await tracker.getGAClientId()
          if (clientId) metadata.ga_client_id = clientId

          const resp: CartResponse = await fnSubmitCheckout(
            testMode.value,
            cartId.value,
            cartToken.value,
            email.value,
            lineItems,
            distinctId,
            location,
            metadata,
            payment
          )

          if (!payment) cartReturnTo.value = location

          setCartIdAndToken(resp)
          if (!resp.ok) setData(resp)

          return resp
        })
        .catch(err => {
          isPending.value = false
          return errorCartResponse(err)
        })
    }

    async function submitInformation(): Promise<CartResponse | undefined> {
      isPending.value = true

      return pq
        .add(async () => {
          await loadInformation()

          if (!(data.value && data.value.cart)) return
          if (!infoCartId.value) return
          if (!infoCartToken.value) return
          if (!items.value) return
          if (!data.value.cart.purchaser) return
          if (!data.value.cart.agreement_responses) return
          if (!data.value.cart.survey_responses) return

          const lineItems = items.value
            .filter(item => item.quantity > 0)
            .map(item => ({
              price: item.price,
              price_id: item.price_id,
              price_type: item.price_type,
              quantity: item.quantity,
              recipients: item.recipients,
              sku: item.sku,
              source: item.source
            }))
          const resp: CartResponse = await fnSubmitInformation(
            testMode.value,
            infoCartId.value,
            infoCartToken.value,
            data.value.cart.purchaser,
            data.value.cart.agreement_responses,
            data.value.cart.survey_responses,
            lineItems
          )

          tracker.plausibleEvent('submitInformation', {
            testMode: testMode.value,
            cartId: infoCartId.value || ''
          })

          return resp
        })
        .catch(err => {
          isPending.value = false
          return errorCartResponse(err)
        })
    }

    async function updateQuantity(
      sku: string,
      quantity: number
    ): Promise<CartResponse | undefined> {
      return pq
        .add(async () => {
          await loadCart()

          if (!(data.value && data.value.cart)) return

          const ind = data.value.cart.line_items.findIndex(
            item => item.sku === sku
          )
          const lineItem =
            ind > -1 ? data.value.cart.line_items[ind] : undefined
          if (lineItem) {
            lineItem.meta = {
              is_pending: true
            }
            lineItem.quantity = quantity
          } else {
            return
          }

          const resp: CartResponse = await fnSetLineItem(
            testMode.value,
            cartId.value,
            cartToken.value,
            sku,
            { quantity }
          )

          setCartIdAndToken(resp)
          setData(resp)

          tracker.plausibleEvent('updateQuantity', {
            testMode: testMode.value,
            cartId: cartId.value || '',
            quantity,
            sku
          })

          return resp
        })
        .catch(err => errorCartResponse(err))
        .finally(() => updates.value.delete(sku))
    }

    async function updatePurchaser(
      contactInfo: ContactInfo,
      agreementResponses: AgreementResponses,
      surveyResponses: SurveyResponses
    ): Promise<CartResponse | undefined> {
      isPending.value = true

      return pq
        .add(async () => {
          await loadInformation()

          if (!(data.value && data.value.cart)) return
          if (!infoCartId.value) return
          if (!infoCartToken.value) return

          const resp: CartResponse = await fnUpdatePurchaser(
            testMode.value,
            infoCartId.value,
            infoCartToken.value,
            contactInfo,
            agreementResponses,
            surveyResponses
          )

          setData(resp)

          tracker.plausibleEvent('updatePurchaser', {
            testMode: testMode.value,
            cartId: infoCartId.value || ''
          })

          return resp
        })
        .catch(err => errorCartResponse(err))
        .finally(() => (isPending.value = false))
    }

    async function updateRecipients(
      sku: string,
      recipientInfos: RecipientInfo[]
    ): Promise<CartResponse | undefined> {
      isPending.value = true

      return pq
        .add(async () => {
          await loadInformation()

          if (!(data.value && data.value.cart)) return
          if (!infoCartId.value) return
          if (!infoCartToken.value) return

          const resp: CartResponse = await fnUpdateRecipients(
            testMode.value,
            infoCartId.value,
            infoCartToken.value,
            sku,
            recipientInfos
          )

          setData(resp)

          tracker.plausibleEvent('updateRecipients', {
            testMode: testMode.value,
            cartId: infoCartId.value || '',
            sku
          })

          return resp
        })
        .catch(err => errorCartResponse(err))
        .finally(() => (isPending.value = false))
    }

    onBeforeMount(() => {
      const url = new URL(window.location.href)
      const hash = window.location.hash
      const cid = url.searchParams.get('cid')

      if (cid && hash.length > 1) {
        try {
          const token = atob(decodeURIComponent(hash.substring(1)))

          if (token.length === 36 && cid.length === 24) {
            if (isInfo) {
              infoCartId.value = 'cart_' + cid
              infoCartToken.value = token
              isPurchase.value = url.searchParams.has('purchase')

              if (infoCartId.value === cartId.value) emptyCart()
            } else {
              emptyCart()
              cartId.value = 'cart_' + cid
              cartToken.value = token
            }

            window.location.hash = ''
          }
        } catch {
          logger.warn('Not a valid cart id or token')
        }
      }
    })

    onMounted(async () => {
      if (new Date().getTime() - sessionUpdateTime.value > 1800000) {
        sessionParams.value = {}
        sessionReferrer.value = ''
      }

      const newSessionParams: Record<string, string> = {}
      const params = new URLSearchParams(window.location.search)
      params.sort()

      const keys = [...new Set(params.keys())]
      for (const key of keys) {
        const value = params.getAll(key).join(',')
        if (key && value) newSessionParams[key] = value
      }

      sessionParams.value = Object.assign(
        {},
        sessionParams.value,
        newSessionParams
      )

      if (document.referrer) {
        const referrerURL = new URL(document.referrer)
        if (
          (referrerURL.host !== window.location.host ||
            referrerURL.pathname.startsWith('/widgets/')) &&
          referrerURL.host !== 'checkout.stripe.com'
        ) {
          sessionReferrer.value = document.referrer
        }
      }

      sessionUpdateTime.value = new Date().getTime()
    })

    if (!isInfo) {
      watch(items, newValue => {
        cartCount.value = (newValue && newValue.length) || 0
      })
    }

    return {
      addItem,
      addItemBySKU,
      beforeUpdateQuantity,
      data,
      email,
      emptyCart,
      fetchCart,
      fetchCheckout,
      fetchInformation,
      hasShopQuantity,
      isEditing,
      isFetching,
      isPending,
      isPurchase,
      items,
      removeItem,
      submitCheckout,
      submitInformation,
      subtotal,
      updateQuantity,
      updatePurchaser,
      updateRecipients
    }
  }
)

function useCartStoreOrThrow() {
  const cartStore = useCartStore()
  if (cartStore == null)
    throw new Error(
      'Please call `useProvideCartStore` on the appropriate parent component'
    )
  return cartStore
}

export { useProvideCartStore, useCartStoreOrThrow }
